import React, { useState, useRef } from 'react';
import styles from './Stats.module.scss';
import Text from "components/Text/Text";
import { ReactComponent as TriangleIcon } from "assets/triangle.svg";
import { ReactComponent as PharmacyIcon } from "assets/rewards/pharmacy1.svg";
import { ReactComponent as PharmacyLockIcon } from "assets/rewards/pharmacylock.svg";
import { ReactComponent as VirtualDispensaryIcon } from "assets/rewards/virtualdispensary1.svg";
import { ReactComponent as VirtualDispensaryLockIcon } from "assets/rewards/virtuallock.svg";
import { ReactComponent as NewProductsIcon } from "assets/rewards/newproducts1.svg";
import { ReactComponent as NewProductsLockIcon } from "assets/rewards/newproductlock.svg";
import { ReactComponent as SpotlightIcon } from "assets/rewards/spotlight1.svg";
import { ReactComponent as SpotlightLockIcon } from "assets/rewards/spotlock.svg";
import { ReactComponent as GIPortfolioIcon } from "assets/rewards/giportifolio1.svg";
import { ReactComponent as GIPortfolioLockIcon } from "assets/rewards/gilock.svg";
import { ReactComponent as DetoxIcon } from "assets/rewards/detox1.svg";
import { ReactComponent as DetoxLockIcon } from "assets/rewards/detoxlock.svg";
import { ReactComponent as RewardIcon } from "assets/rewards/reward1.svg";
import { ReactComponent as RewardLockIcon } from "assets/rewards/rewardlock.svg";
import { store } from "stores/Store";
import useExternalApiService from "helpers/ExternalApiService";
import {formatMoney, formatMoneyWithZero, formatNumber } from "helpers";
import { ReactComponent as InfoIcon } from "assets/info.svg";
import { ReactComponent as ExpandIcon } from "assets/expand.svg";

import { logEvent } from '@amplitude/analytics-browser'; // ← browser SDK

const Stats = () => {
  const [stats, setStats] = React.useState<any>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { postData } = useExternalApiService(apiUrl);
  const [keystats, setKeystats] = React.useState<any>(null);
  const [topProducts, setTopProducts] = React.useState<any>(null);
  const [growingProducts, setGrowingProducts] = React.useState<any>(null);
  const [decliningProducts, setDecliningProducts] = React.useState<any>(null);
  const [popularNotBought, setPopularNotBought] = React.useState<any>(null);
  const [rewardsUnlocked, setRewardsUnlocked] = React.useState<any>(null);
  const [checked, setChecked] = React.useState(false);

  // Conditional rendering logic to lock down 
  const dataSource = store.currentPractitioner?.data_source;
  const status = store.currentPractitioner?.status;

  const shouldShowWarning =
    dataSource === "PRACTITIONER_ACCOUNT_VIEW_NM" ||
    status === "LEAD-INCOMPLETE" ||
    status === "LEAD-READY2SUBMIT";

  const practitionerName = store.currentPractitioner?.primary_name ?? "";

  React.useEffect(() => {
    if (!practitionerName) return;

    const getKeystats = async () => {
      const response = await postData('/api/e/call_winback_keystats_by_practitioner_name', {practitioner_name: store.currentPractitioner?.primary_name ?? ''});

      setKeystats(response.payload);
    }
    getKeystats();
  }, [practitionerName]);

  React.useEffect(() => {
    if (!practitionerName) return;

    const getTopProducts = async () => {
      const response = await postData('/api/e/call_winback_top_products_by_practitioner_name', {practitioner_name: store.currentPractitioner?.primary_name ?? ''});

      setTopProducts(response.payload);
    }
    getTopProducts();
  }, [practitionerName]);

  React.useEffect(() => {
    if (!practitionerName) return;

    const getGrowingProducts = async () => {
      const response = await postData('/api/e/call_winback_growing_products_by_practitioner_name', {practitioner_name: store.currentPractitioner?.primary_name ?? ''});

      setGrowingProducts(response.payload);
    }
    getGrowingProducts();
  }, [practitionerName]);

  React.useEffect(() => {
    if (!practitionerName) return;

    const getDecliningProducts = async () => {
      const response = await postData('/api/e/call_winback_declining_products_by_practitioner_name', {practitioner_name: store.currentPractitioner?.primary_name ?? ''});
      console.log(response.payload);
      setDecliningProducts(response.payload);
    }
    getDecliningProducts();
  }, [practitionerName]);

  React.useEffect(() => {
    if (!practitionerName) return;

    if (checked) {
      const getPopularNotBought = async () => {
        const response = await postData('/api/e/call_winback_popular_not_bought_by_practitioner_name', {practitioner_name: store.currentPractitioner?.primary_name ?? '', specialty: store.currentPractitioner?.specialty});
        console.log(response.payload);
        setPopularNotBought(response.payload);
      }
      getPopularNotBought();
    }else{
      const getPopularNotBought = async () => {
        const response = await postData('/api/e/call_winback_popular_not_bought_by_practitioner_name', {practitioner_name: store.currentPractitioner?.primary_name ?? '', specialty: null});
        console.log(response.payload);
        setPopularNotBought(response.payload);
      }
      getPopularNotBought();
    }
  }, [checked, practitionerName]);

  React.useEffect(() => {
    if (!practitionerName) return;

    const getRewardsUnlocked = async () => {
      const response = await postData('/api/e/call_winback_business_priorities_icons_by_practitioner_name', {practitioner_name: store.currentPractitioner?.primary_name ?? ''});

      setRewardsUnlocked(response.payload);
    }
    getRewardsUnlocked();
  }, [practitionerName, ]);

  const [sections, setSections] = useState({
    keyStats: false,
    topProducts: false,
    growingProducts: false,
    decliningProducts: false,
    popularNotBought: false
  });

  const sectionRefs = {
    keyStats: useRef<HTMLDivElement>(null),
    topProducts: useRef<HTMLDivElement>(null),
    growingProducts: useRef<HTMLDivElement>(null),
    decliningProducts: useRef<HTMLDivElement>(null),
    popularNotBought: useRef<HTMLDivElement>(null)
  };

  const toggleSection = (section: keyof typeof sections) => {
    setSections(prev => {
      // If the clicked section is already open, just close it
      if (prev[section]) {
        return {
          ...prev,
          [section]: false
        };
      }
      const sectionTrack = 'Question asked: ' + sections;
      console.log("sectionTrack: ", sectionTrack );
      trackClick(sectionTrack);
      
      // If the clicked section is closed, open it and close all others
      return {
        keyStats: false,
        topProducts: false,
        growingProducts: false,
        decliningProducts: false,
        popularNotBought: false,
        [section]: true
      };
    });
    
    // Scroll to the section after a short delay to ensure it's expanded
    setTimeout(() => {
      sectionRefs[section].current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const trackClick = (buttonName: string) => {
        const label = buttonName
        logEvent(label, { buttonName });
  };

  React.useEffect(() => {
    const getStats = async () => {
      const mockStats = {
        title: "Growing Gem",
        sizeTier: "Tier 4",
        sizeTierDescription: "Sales between  $1,000  to $2,500 in Q1 '24",
        growthTier: "Tier 7",
        growthTierDescription: "Increased $1,000  to $2,500 in Q4 '23 to Q1 '24",
        mtd_sales: keystats?.mtd_sales,
        ytd_sales: keystats?.ytd_sales,
        mtd_diff: keystats?.mtd_diff,
        ytd_diff: keystats?.ytd_diff,
        last_updated: '2021-01-01',

        top_products: [
          { name: 'GI Revive', price: 56000 },
          { name: 'CoQnol', price: 30000 },
          { name: 'Spotlight', price: 30000 },
          { name: 'Detox Plus', price: 25000 },
          { name: 'GI Portfolio', price: 20000 }
        ],
        growing_products: [
          { name: 'GI Revive', price: 56000 },
          { name: 'CoQnol', price: 30000 },
          { name: 'Spotlight', price: 30000 }
        ],
        declining_products: [
          { name: 'Detox Plus', price: 25000 },
          { name: 'GI Portfolio', price: 20000 }
        ],
        popular_not_bought: [
          { name: 'New Product A', specialty: 'GI' },
          { name: 'New Product B', specialty: 'Cardiology' },
          { name: 'New Product C', specialty: 'GI' }
        ]
      }
      setStats(mockStats);
      // const response = await postData('/api/e/call_winback_keystats_by_practitioner_name', {
      //   practitioner_name: store.currentPractitioner?.primary_name ?? ''
      // });
      // setStats(response.payload);
    };
    getStats();
  }, [keystats]);

  if (shouldShowWarning) {
    return (
      <div className={styles["stats-warning-container"]}>
        <p className={styles["stats-warning-text"]}>
          To view these detailed Sales Stats, this practitioner's baseball card needs to be matched in Netsuite.                    
        </p>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerWrapper}>

        <div className={styles.headerTextWrapper}>
          {/*
          <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
              <span style={{fontFamily: 'Manrope', fontSize: '20px', fontWeight: '600'}}>{stats?.title}</span>
              <div 
                className={styles.infoIconWrapper}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                onTouchStart={() => setShowTooltip(true)}
                onTouchEnd={() => setShowTooltip(false)}
              >
                <InfoIcon width={25} height={25} />
                {showTooltip && (
                  <div 
                    className={styles.tooltip}
                    onTouchStart={(e) => e.stopPropagation()}
                  >
                    <div className={styles.tooltipContent}>
                      <div>
                        <Text type="header">{`Size: ${stats?.sizeTier}`}</Text>
                        <Text type="subHeader">
                          {stats?.sizeTierDescription}
                        </Text>
                      </div>
                      <div>
                        <Text type="header">{`Growth: ${stats?.growthTier}`}</Text>
                        <Text type="subHeader">
                          {stats?.growthTierDescription}
                        </Text>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          */}
          <span style={{fontSize: '12px',  marginTop: '5px', marginBottom: '10px'}}>Business priorities relevant to this practitioner:</span>
        </div>
        {/* <div style={{textAlign: 'left'}} className={styles.twoColumnWrapper}>
          <div>
            <Text type="header">{`Size: ${stats?.sizeTier}`}</Text>
            <Text type="subHeader">
              {stats?.sizeTierDescription}
            </Text>
          </div>
          <div>
            <Text type="header">{`Growth: ${stats?.growthTier}`}</Text>
            <Text type="subHeader">
              {stats?.growthTierDescription}
            </Text>
          </div>
        </div> */}
      </div>
      {/* <p className={styles.headerText} style={{ alignSelf: 'center' }}>Distributor Data last updated as of {stats?.last_updated}</p> */}

      <div className={styles.rewardsWrapper}>
        <div className={styles.rewardItem}>
          {rewardsUnlocked?.has_purchased_herosku ? 
            <RewardIcon width={27} height={27} /> : 
            <RewardLockIcon width={27} height={27} />
          }
          <Text textAlign="center" color={rewardsUnlocked?.has_purchased_herosku ? undefined : '#D9D9D9'}>Bought HeroSKUs</Text>
        </div>
        <div className={styles.rewardItem}>
          {rewardsUnlocked?.is_pharmacist ? 
            <PharmacyIcon width={27} height={27} /> : 
            <PharmacyLockIcon width={27} height={27} />
          }
          <Text textAlign="center" color={rewardsUnlocked?.is_pharmacist ? undefined : '#D9D9D9'}>Pharmacist</Text>
        </div>
        <div className={styles.rewardItem}>
          {rewardsUnlocked?.has_virtual_dispensary ? 
            <VirtualDispensaryIcon width={27} height={27} /> : 
            <VirtualDispensaryLockIcon width={27} height={27} />
          }
          <Text textAlign="center" color={rewardsUnlocked?.has_virtual_dispensary ? undefined : '#D9D9D9'}>Virtual Dispensary</Text>
        </div>
        <div className={styles.rewardItem}>
          {rewardsUnlocked?.has_bought_new_products ? 
            <NewProductsIcon width={27} height={27} /> : 
            <NewProductsLockIcon width={27} height={27} />
          }
          <Text textAlign="center" color={rewardsUnlocked?.has_bought_new_products ? undefined : '#D9D9D9'}>Bought New Products</Text>
        </div>
        <div className={styles.rewardItem}>
          {rewardsUnlocked?.has_bought_spotlight ? 
            <SpotlightIcon width={27} height={27} /> : 
            <SpotlightLockIcon width={27} height={27} />
          }
          <Text textAlign="center" color={rewardsUnlocked?.has_bought_spotlight ? undefined : '#D9D9D9'}>Bought Spotlight</Text>
        </div>
        <div className={styles.rewardItem}>
          {rewardsUnlocked?.has_bought_spotlight ? 
            <GIPortfolioIcon width={27} height={27} /> : 
            <GIPortfolioLockIcon width={27} height={27} />
          }
          <Text textAlign="center" color={rewardsUnlocked?.has_bought_spotlight ? undefined : '#D9D9D9'}>Bought GI Portfolio</Text>
        </div>
        <div className={styles.rewardItem}>
          {rewardsUnlocked?.has_bought_detox ? 
            <DetoxIcon width={27} height={27} /> : 
            <DetoxLockIcon width={27} height={27} />
          }
          <Text textAlign="center" color={rewardsUnlocked?.has_bought_detox ? undefined : '#D9D9D9'}>Bought Detox</Text>
        </div>
        <div className={styles.rewardItem}>
          {rewardsUnlocked?.is_rewards_member ? 
            <RewardIcon width={27} height={27} /> : 
            <RewardLockIcon width={27} height={27} />
          }
          <Text textAlign="center" color={rewardsUnlocked?.is_rewards_member ? undefined : '#D9D9D9'}>Rewards Member</Text>
        </div>
      </div>
      <div className={styles.sectionWrapper} ref={sectionRefs.keyStats}>
        <div className={styles.headerContainer} onClick={() => toggleSection('keyStats')}>
          <p className={styles.headerText}>Key Stats: Practitioner Insights</p>
          <ExpandIcon 
            className={`${styles.expandIcon} ${sections.keyStats ? styles.expanded : ''}`}
            style={{width: '15px', height: '15px'}}
          />
        </div>
        {sections.keyStats && (
          <div></div>
        )}
        <div style={{marginTop: '10px'}} className={styles.twoColumnWrapper}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'row' }}>
              {/*<span style={{ color: keystats?.mtd_diff < 0 ? '#E64B37' : '#00DC49CC', fontSize: '20px', fontWeight: '500', paddingRight: '10px' }}>*/}
              <span style={{ fontSize: '20px', fontWeight: '500', paddingRight: '10px' }}>
                {formatMoneyWithZero(keystats?.mtd_sales)}
              </span>
              {/* {keystats?.mtd_diff < 0 ? (
                <TriangleIcon width="20px" height="20px" fill="#E64B37" />
              ) : (
                <TriangleIcon width="20px" height="20px" fill="#00DC49CC" style={{ transform: 'rotate(180deg)' }} />
              )} */}
            </div>
            <div>
              <Text fontWeight="500" fontSize="12px" textAlign="center">MTD Sales</Text>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'row' }}>
              {/*<span style={{ color: keystats?.ytd_diff < 0 ? '#E64B37' : '#00DC49CC', fontSize: '20px', fontWeight: '500', paddingRight: '10px' }}>*/}
              <span style={{ fontSize: '20px', fontWeight: '500', paddingRight: '10px' }}>
                {formatMoneyWithZero(keystats?.ytd_sales)}
              </span>
              {/* {keystats?.ytd_diff < 0 ? (
                <TriangleIcon width="20px" height="20px" fill="#E64B37" />
              ) : (
                <TriangleIcon width="20px" height="20px" fill="#00DC49CC" style={{ transform: 'rotate(180deg)' }} />
              )} */}
            </div>
            <div>
              <Text fontWeight="500" fontSize="12px" textAlign="center">YTD Sales</Text>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.whatCanYouDoWrapper}>
        <span style={{fontWeight: '600', marginBottom: '15px'}}>What can you do?</span> 
        <div className={styles.whatCanYouDoItem} onClick={() => toggleSection('topProducts')}>
        1. <a>What is contributing to increase in sales?</a>
        </div>
        <div className={styles.whatCanYouDoItem} onClick={() => toggleSection('growingProducts')}>
        2. <a>What are growing products?</a>
        </div>
        <div className={styles.whatCanYouDoItem} onClick={() => toggleSection('decliningProducts')}>
        3. <a>Why are sales declining?</a>
        </div>
        <div className={styles.whatCanYouDoItem} onClick={() => toggleSection('popularNotBought')}>
        4. <a>What are popular products this practitioner are not buying?</a>
        </div>
      </div>
      <div className={styles.sectionWrapper} ref={sectionRefs.topProducts}>
        <div className={styles.headerContainer} onClick={() => toggleSection('topProducts')}>
          <p className={styles.headerText}>Top Products Bought (Last 12 months)</p>
          <ExpandIcon 
            className={`${styles.expandIcon} ${sections.topProducts ? styles.expanded : ''}`}
            style={{width: '15px', height: '15px'}}
          />
        </div>
        {sections.topProducts && (
          <div className={styles.productsList}>
            {topProducts?.top_skus_purchased_last12months?.map((product: { ITEM_NAME: string; SALES: number }, index: number) => (
              <div key={index} className={styles.productRow}>
                <div className={styles.productName}>
                  <div style={{width: '10px', textAlign: 'center'}}>{index + 1}.</div>
                  <div style={{paddingLeft: '3px'}}>{product.ITEM_NAME}</div>
                </div>
                <div className={styles.productPrice}>
                  <div>$</div>
                  <span>{formatNumber(product.SALES.toString().split('.')[0])}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.sectionWrapper} ref={sectionRefs.growingProducts}>
        <div className={styles.headerContainer} onClick={() => toggleSection('growingProducts')}>
          <p className={styles.headerText}>Growing Products (Last 12 months)</p>
          <ExpandIcon 
            className={`${styles.expandIcon} ${sections.growingProducts ? styles.expanded : ''}`}
            style={{width: '15px', height: '15px'}}
          />
        </div>
        {sections.growingProducts && (
          <div className={styles.productsList}>
            {growingProducts?.map((product: { ITEM_NAME: string; GROWTH: number }, index: number) => (
              <div key={index} className={styles.productRow}>
                  <div className={styles.productName}>
                    <div style={{width: '10px', textAlign: 'center'}}>{index + 1}.</div>
                    <div style={{paddingLeft: '3px'}}>{product.ITEM_NAME}</div>
                  </div>
                  <div className={styles.productPrice}>
                    <div>$</div>
                    <span>{formatNumber(product.GROWTH.toString().split('.')[0])}</span>
                  </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.sectionWrapper} ref={sectionRefs.decliningProducts}>
        <div className={styles.headerContainer} onClick={() => toggleSection('decliningProducts')}>
          <p className={styles.headerText}>Declining Products (Last 12 months)</p>
          <ExpandIcon 
            className={`${styles.expandIcon} ${sections.decliningProducts ? styles.expanded : ''}`}
            style={{width: '15px', height: '15px'}}
          />
        </div>
        {sections.decliningProducts && (
          <div className={styles.productsList}>
            {decliningProducts?.map((product: { ITEM_NAME: string; DECLINE: number }, index: number) => (
              <div key={index} className={styles.productRow}>
                <div className={styles.productName}>
                  <div style={{width: '10px', textAlign: 'center'}}>{index + 1}.</div>
                  <div style={{paddingLeft: '3px'}}>{product.ITEM_NAME}</div>
                </div>
                <div className={styles.productPrice}>
                  <div>-$</div>
                  <span>{formatNumber(product.DECLINE.toString().replace('-', '').split('.')[0])}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.sectionWrapper} ref={sectionRefs.popularNotBought}>
        <div className={styles.headerContainer} onClick={() => toggleSection('popularNotBought')}>
          <p className={styles.headerText}>Popular Products Not Bought (Last 12 months)</p>
          <ExpandIcon 
            className={`${styles.expandIcon} ${sections.popularNotBought ? styles.expanded : ''}`}
            style={{width: '15px', height: '15px'}}
          />
        </div>
        {sections.popularNotBought && (
          <div className={styles.popularNotBoughtWrapper}>
            <div className={styles.checkboxWrapper}>
              <input 
                type="checkbox" 
                id="sameSpecialty" 
                onChange={(e) => {
                  setChecked(e.target.checked);
                }}
              />
              <label htmlFor="sameSpecialty" style={{whiteSpace: 'nowrap'}}>By Same Specialty</label>
            </div>
            <div className={styles.productsList}>
              {popularNotBought?.map((product: any, index: number) => (
                <div key={index} className={styles.productRow}>
                  <div className={styles.productName}>
                    <div style={{width: '10px', textAlign: 'center'}}>{index + 1}.</div>
                    <div style={{paddingLeft: '3px'}}>{product}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Stats;
